import React, { memo, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface HeroSlide {
  id: number;
  desktopTitle: string;
  mobileTitle: string;
  mobileSubtitle: string;
  subtitle: string;
  desktopDescription: string;
  ctaLabel: string;
  destination: string;
  image: string;
  mobileBackgroundClass: string;
  desktopBackgroundClass: string;
}
const slides: HeroSlide[] = [{
  id: 1,
  desktopTitle: 'Up to 25% off daily wellness',
  mobileTitle: 'MAIN HERO BANNER',
  mobileSubtitle: 'Hero banner',
  subtitle: 'Vitamins, essentials and everyday care',
  desktopDescription: 'Shop trusted supplements selected for your routine.',
  ctaLabel: 'Shop vitamins',
  destination: '/category/vitamins',
  image: '',
  mobileBackgroundClass: 'bg-gradient-to-br from-gray-400 via-gray-300 to-gray-200',
  desktopBackgroundClass: 'md:bg-none md:bg-[#1E2B55]'
}, {
  id: 2,
  desktopTitle: 'Build your best routine',
  mobileTitle: 'SPECIAL OFFERS',
  mobileSubtitle: 'DISCOVER OUR LATEST\nPRODUCTS & DEALS',
  subtitle: 'Performance nutrition for every goal',
  desktopDescription: 'Explore protein, pre-workout and recovery essentials.',
  ctaLabel: 'Shop performance',
  destination: '/category/pre-workout',
  image: '',
  mobileBackgroundClass: 'bg-gradient-to-br from-blue-300 to-blue-100',
  desktopBackgroundClass: 'md:bg-none md:bg-[#0f766e]'
}, {
  id: 3,
  desktopTitle: 'New season, fresh energy',
  mobileTitle: 'SUMMER COLLECTION',
  mobileSubtitle: 'NEW ARRIVALS FOR\nTHE SEASON',
  subtitle: 'New arrivals for your health goals',
  desktopDescription: 'Discover new formulas from brands you trust.',
  ctaLabel: 'Explore new arrivals',
  destination: '/category/best-sellers',
  image: '',
  mobileBackgroundClass: 'bg-gradient-to-br from-amber-300 via-yellow-200 to-amber-100',
  desktopBackgroundClass: 'md:bg-none md:bg-[#9a5a11]'
}, {
  id: 4,
  desktopTitle: 'More value in every basket',
  mobileTitle: 'EXCLUSIVE DEALS',
  mobileSubtitle: 'LIMITED TIME\nOFFERS',
  subtitle: 'Limited-time deals, ready to shop',
  desktopDescription: 'Find current offers before they are gone.',
  ctaLabel: 'View offers',
  destination: '/offers',
  image: '',
  mobileBackgroundClass: 'bg-green-200',
  desktopBackgroundClass: 'md:bg-[#166534]'
}];
export const HeroBanner = memo(function HeroBanner() {
  const navigate = useNavigate();
  const shouldReduceMotion = useReducedMotion();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);
  const sideBannerImage = '';
  useEffect(() => {
    const interval = window.setInterval(() => {
      setDirection(1);
      setCurrentSlide(previous => previous === slides.length - 1 ? 0 : previous + 1);
    }, 5000);
    return () => window.clearInterval(interval);
  }, []);
  const goToSlide = useCallback((index: number) => {
    setDirection(index > currentSlide ? 1 : -1);
    setCurrentSlide(index);
  }, [currentSlide]);
  const nextSlide = useCallback(() => {
    setDirection(1);
    setCurrentSlide(previous => previous === slides.length - 1 ? 0 : previous + 1);
  }, []);
  const previousSlide = useCallback(() => {
    setDirection(-1);
    setCurrentSlide(previous => previous === 0 ? slides.length - 1 : previous - 1);
  }, []);
  const slide = slides[currentSlide];
  return <>
      <div className="container mx-auto px-0 md:px-4" data-id="element-2474" data-mp="1ze7d17aaxppz">
        <div className="grid grid-cols-1 gap-0 md:grid-cols-[minmax(0,3fr)_minmax(260px,1fr)] md:items-stretch md:gap-4" data-id="element-2475" data-mp="1lk6k4u616wxj">
          <section className="relative -mt-[60px] overflow-hidden md:mt-0 md:rounded-lg" aria-label="Featured offers" data-id="element-2476" data-mp="1pkgo61a9jd9r">
            <div className="relative aspect-[3/4] w-full overflow-hidden md:aspect-[21/9]" data-id="element-2477" data-mp="2i2ucna9plmw6">
              <AnimatePresence initial={false} custom={direction} data-id="element-2478" data-mp="2i4p1gzo3pz5j">
                <motion.div key={slide.id} custom={direction} initial={shouldReduceMotion ? false : {
                opacity: 0,
                x: direction > 0 ? '8%' : '-8%'
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={shouldReduceMotion ? {
                opacity: 0
              } : {
                opacity: 0,
                x: direction > 0 ? '-8%' : '8%'
              }} transition={{
                duration: shouldReduceMotion ? 0 : 0.26,
                ease: [0.22, 1, 0.36, 1]
              }} className={`absolute inset-0 h-full w-full ${slide.mobileBackgroundClass} ${slide.desktopBackgroundClass}`} data-id="element-2479" data-mp="ug3vmw2t77e3">
                  {slide.image && <img src={slide.image} alt="" className="absolute inset-0 h-full w-full object-cover" data-id="element-2480" data-mp="13cupd1tw2a5i" />}
                  <div className="absolute inset-0 flex max-w-xl flex-col justify-end p-6 pb-24 pt-[var(--header-height-mobile)] text-white md:justify-center md:p-10" data-id="element-2481" data-mp="zbsx54ye2yd1">
                    <h2 className="text-4xl font-black leading-tight tracking-tight md:text-5xl" data-id="element-2482" data-mp="1r38h0r8ka6zk">
                      <span className="md:hidden" data-id="element-2483" data-mp="39t4fqaqf6plq">{slide.mobileTitle}</span>
                      <span className="hidden md:block" data-id="element-2484" data-mp="3erny615t1j9c">{slide.desktopTitle}</span>
                    </h2>
                    <p className="mt-2 whitespace-pre-line text-xl font-semibold leading-snug text-white/90 md:text-2xl" data-id="element-2485" data-mp="3irgckuhvqjmm">
                      <span className="md:hidden" data-id="element-2486" data-mp="3nsm7myz0iizb">{slide.mobileSubtitle}</span>
                      <span className="hidden md:block" data-id="element-2487" data-mp="3spmdb8ry9jpw">{slide.subtitle}</span>
                    </p>
                    <p className="mt-3 hidden max-w-md text-sm leading-6 text-white/80 md:block" data-id="element-2488" data-mp="1ijlqg879bf5">
                      {slide.desktopDescription}
                    </p>
                    <button type="button" onClick={() => navigate(slide.destination)} className="mt-6 hidden w-fit rounded-md bg-white px-5 py-3 text-sm font-bold text-[#1E2B55] transition-colors duration-150 hover:bg-[#f2f4fa] md:block" data-id="element-2489" data-mp="aco6chwqpqm8">
                      {slide.ctaLabel}
                    </button>
                    <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-white/90 md:hidden" data-id="element-2490" data-mp="2bf5tl7sp8oaz">
                      Mobile size: 750×1000 px
                    </p>
                    <p className="mt-4 hidden text-xs font-semibold tracking-wide text-white/80 md:block" data-id="element-2491" data-mp="2ld3ilxidup4n">
                      Desktop banner size: 2100×900 px
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="absolute left-4 top-1/2 z-20 hidden -translate-y-1/2 md:block" data-id="element-2492" data-mp="2v4ppxirlo424">
                <button onClick={previousSlide} className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white transition-colors duration-150 hover:bg-white/30" aria-label="Previous offer" data-id="element-2493" data-mp="391mok6dymptk">
                  <ChevronLeftIcon className="h-5 w-5" data-id="element-2494" data-mp="1eq7ytpky8qdt" />
                </button>
              </div>
              <div className="absolute right-4 top-1/2 z-20 hidden -translate-y-1/2 md:block" data-id="element-2495" data-mp="19rmzdsdiuaqf">
                <button onClick={nextSlide} className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white transition-colors duration-150 hover:bg-white/30" aria-label="Next offer" data-id="element-2496" data-mp="3lezwpvlkvq8m">
                  <ChevronRightIcon className="h-5 w-5" data-id="element-2497" data-mp="3glxay9n1ll2z" />
                </button>
              </div>

              <div className="absolute bottom-4 left-0 right-0 z-20 flex justify-center gap-2" data-id="element-2498" data-mp="32m5s9vlkzgin">
                {slides.map((item, index) => <button key={item.id} type="button" onClick={() => goToSlide(index)} className={`h-2.5 rounded-full transition-[width,background-color] duration-150 ${currentSlide === index ? 'w-6 bg-white' : 'w-2.5 bg-white/50 hover:bg-white/75'}`} aria-label={`Go to offer ${index + 1}`} aria-current={currentSlide === index ? 'true' : undefined} data-id="element-2499" data-mp="37m0gfrdz1s00" data-mpi={index} />)}
              </div>
            </div>
          </section>

          <aside className="hidden overflow-hidden rounded-lg bg-[#b45309] md:block" aria-label="Featured delivery offer" data-id="element-2500" data-mp="3as46xp4dnmsw">
            <div className="relative flex h-full min-h-[250px] flex-col items-center justify-center p-7 text-center text-white" data-id="element-2501" data-mp="360vyavavg5gs">
              {sideBannerImage && <img src={sideBannerImage} alt="" className="absolute inset-0 h-full w-full object-cover" data-id="element-2502" data-mp="31102hjcbvkcb" />}
              <div className="relative" data-id="element-2503" data-mp="2s261lgrys7sy">
                <p className="text-sm font-bold text-white/85" data-id="element-2504" data-mp="2s138d2765t9v">DELIVERY BENEFIT</p>
                <h2 className="mt-3 text-4xl font-black" data-id="element-2505" data-mp="2n29vnbzpi6ar">Free delivery</h2>
                <p className="mt-3 text-sm leading-6 text-white/90" data-id="element-2506" data-mp="2i3yo57wvw7vp">On qualifying orders to your selected location.</p>
                <button type="button" onClick={() => navigate('/offers')} className="mt-6 rounded-md bg-white px-4 py-2.5 text-sm font-bold text-[#8a4a07] transition-colors duration-150 hover:bg-[#fff8e6]" data-id="element-2507" data-mp="lc86ll4gkwm6">
                  See delivery offers
                </button>
                <p className="mt-5 text-[11px] font-semibold text-white/75" data-id="element-2508" data-mp="nua699r9hm1x">Desktop side banner: 700×900 px</p>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <div className="py-3 md:hidden" data-id="element-2509" data-mp="xo0rm5s3dgde">
        <div className="container mx-auto px-4" data-id="element-2510" data-mp="12lkajizqihbo">
          <div className="relative aspect-square overflow-hidden rounded-lg bg-red-500" data-id="element-2511" data-mp="12n3ov41tyiat">
            {sideBannerImage && <img src={sideBannerImage} alt="" className="absolute inset-0 h-full w-full object-cover" data-id="element-2512" data-mp="4zmv5ugnis3y" />}
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center text-white" data-id="element-2513" data-mp="9y6h3uv8w3wb">
              <span className="mb-4 rounded-full bg-white px-3 py-1 text-sm font-bold text-red-600" data-id="element-2514" data-mp="ju9ll7dkkemf">DIMENSIONS</span>
              <h2 className="mb-2 text-4xl font-bold" data-id="element-2515" data-mp="1qbejvqkuhnr6">25%</h2>
              <p className="mb-4 text-lg font-semibold" data-id="element-2516" data-mp="1qcxy7bmxxoqb">WIDTH<br data-id="element-2517" data-mp="1v8n3zwpk01v2" />VERTICAL BANNER</p>
              <p className="text-base" data-id="element-2518" data-mp="1vbstve60b7ru">ASPECT RATIO: 1:1</p>
            </div>
          </div>
        </div>
      </div>
    </>;
});